"use client";

import { useEffect } from "react";
import { injectExternalScript } from "@/helpers/injectInlineScript";

const UTM_DEFAULTS = {
  utm_source: "website",
  utm_campaign: "",
  utm_medium: "",
  utm_media: "",
  utm_keyword: "",
  utm_placement: "",
  utm_term: "",
};

const WEBENGAGE_SDK_SRC =
  "https://widgets.in.webengage.com/js/webengage-min-v-6.0.js";

function setUtmSessionParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const cached = JSON.parse(sessionStorage.getItem("utm_params") || "{}");

    const values = JSON.stringify({
      utm_source: params.get("utm_source") || cached.utm_source || UTM_DEFAULTS.utm_source,
      utm_campaign: params.get("utm_campaign") || cached.utm_campaign || UTM_DEFAULTS.utm_campaign,
      utm_medium: params.get("utm_medium") || cached.utm_medium || UTM_DEFAULTS.utm_medium,
      utm_media: params.get("utm_media") || cached.utm_media || UTM_DEFAULTS.utm_media,
      utm_keyword: params.get("utm_keyword") || cached.utm_keyword || UTM_DEFAULTS.utm_keyword,
      utm_placement: params.get("utm_placement") || cached.utm_placement || UTM_DEFAULTS.utm_placement,
      utm_term: params.get("utm_term") || cached.utm_term || UTM_DEFAULTS.utm_term,
    });

    sessionStorage.setItem("utm_params", values);
    sessionStorage.setItem("AMSID", params.get("ams_id") || "");
    sessionStorage.setItem("utm_campaign_2", params.get("utm_campaign_2") || "");
  } catch {
    // Keep page execution resilient if storage/query parsing fails.
  }
}

/** Queue shim so early track() calls are not dropped before SDK arrives. */
function installWebEngageShim(): void {
  const webengageWindow = window as any;

  if (webengageWindow.webengage?.__v) return;
  if (webengageWindow.webengage?.__queue) return;

  const queueObj: any = {
    __queue: [] as unknown[],
    is_spa: 1 as const,
    __v: "6.0",
    user: {},
  };
  webengageWindow.webengage = queueObj;

  const shimTargets = [
    "init",
    "options",
    "track",
    "screen",
    "onReady",
    "feedback.render",
    "feedback.clear",
    "feedback.abort",
    "survey.render",
    "survey.clear",
    "survey.abort",
    "notification.render",
    "notification.clear",
    "notification.abort",
    "user.identify",
    "user.login",
    "user.logout",
    "user.setAttribute",
  ];

  for (const path of shimTargets) {
    const keys = path.split(".");
    let node: Record<string, unknown> = queueObj;
    for (let i = 0; i < keys.length - 1; i += 1) {
      const key = keys[i];
      node[key] = (node[key] as Record<string, unknown>) || {};
      node = node[key] as Record<string, unknown>;
    }
    const method = keys[keys.length - 1];
    node[method] = (...args: unknown[]) => {
      queueObj.__queue.push([path, args]);
    };
  }
}

function initWebEngage(licenseKey: string): void {
  if (!licenseKey) return;
  (window as any).webengage?.init?.(`in~${licenseKey}`);
}

type Props = {
  webEngageKey?: string;
};

/**
 * UTM + WebEngage after hydration (not behind the 3s third-party gate).
 * Injects via DOM — next/script inside a client component warns in React 19 / next dev.
 */
export default function RootLayoutDeferredScripts({ webEngageKey }: Props) {
  useEffect(() => {
    setUtmSessionParams();
    if (!webEngageKey) return;

    installWebEngageShim();
    injectExternalScript("webengage-sdk", WEBENGAGE_SDK_SRC, () => {
      initWebEngage(webEngageKey);
    });
  }, [webEngageKey]);

  return null;
}
